import { createFileRoute, Outlet, useLocation } from "@tanstack/react-router";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { usePageTitle } from "../../../../../contexts/PageTitleContext";
import {
  Box,
  Tabs,
  Tab,
  IconButton,
  Typography,
  Stack,
  Container,
  CircularProgress,
} from "@mui/material";
import {
  ArrowBack,
  Timeline,
  ListAlt,
  Description,
  HistoryEdu,
  Settings,
  Warning,
} from "@mui/icons-material";
import { useAuth } from "../../../../../auth";

import {
  getFindUserQueryOptions,
  getGetTaskVariablesIgnoringTenantQueryOptions,
  getGetProcessInstanceByIdIgnoringTenantQueryOptions,
  getGetProcessInstanceIncidentsQueryOptions,
} from "../../../../../api/bpm/endpoints";
import { useQuery } from "@tanstack/react-query";
import ProcessHeader from "./components/ProcessHeader";
import { decodeNavParams } from "../../../../../utils/navParams";

import { z } from "zod";

const historySearchSchema = z.object({
  from: z
    .enum(["tasks", "all-tasks", "processes"])
    .optional()
    .catch("processes"),
  fromParams: z.string().optional().catch(""),
});

export const Route = createFileRoute(
  "/__auth/processes/$processInstanceId/history",
)({
  component: TaskHistoryLayout,
  validateSearch: (search) => historySearchSchema.parse(search),
  staticData: { title: "routes.processHistory" },
});

function TaskHistoryLayout() {
  const { processInstanceId } = Route.useParams();
  const navigate = Route.useNavigate();
  const search = Route.useSearch();
  const location = useLocation();

  const { currentTenant, user } = useAuth();
  const { setTitlePrefix } = usePageTitle();
  const { t } = useTranslation();

  const {
    data: processInstanceResponse,
    isLoading: isLoadingProcess,
    error,
  } = useQuery({
    ...getGetProcessInstanceByIdIgnoringTenantQueryOptions(processInstanceId),
    enabled: !!processInstanceId,
  });
  const processInstance =
    (processInstanceResponse as any)?.data || processInstanceResponse;

  const handleBack = () => {
    if (search.from === "tasks" && search.fromParams) {
      const params = decodeNavParams(search.fromParams);
      navigate({ to: "/tasks", search: params as any });
    } else if (search.from === "all-tasks" && search.fromParams) {
      const params = decodeNavParams(search.fromParams);
      navigate({ to: "/tasks/all", search: params as any });
    } else if (search.from === "processes" && search.fromParams) {
      const params = decodeNavParams(search.fromParams);
      navigate({ to: "/processes", search: params as any });
    } else {
      const prevParams = search.fromParams
        ? decodeNavParams(search.fromParams) || {}
        : {};
      navigate({
        to: "/processes",
        search:
          Object.keys(prevParams).length > 0
            ? (prevParams as any)
            : {
                page: 1,
                size: 10,
                orderBy: "startTime",
                order: "desc",
              },
      });
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleString("pt-BR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const tenant =
    processInstance?.tenantId ||
    currentTenant ||
    user?.tenant ||
    "default-domain";
  const startUserId = processInstance?.startUserId;
  const { data: starterUser } = useQuery({
    ...getFindUserQueryOptions(tenant, startUserId || "", {
      query: {
        enabled: !!tenant && !!startUserId,
      },
    }),
  });
  const { data: processVariablesResponse } = useQuery({
    ...getGetTaskVariablesIgnoringTenantQueryOptions(
      { processInstanceIds: [processInstanceId] },
      {
        query: {
          enabled: !!processInstanceId,
        },
      },
    ),
  });

  const { data: incidentsResponse } = useQuery({
    ...getGetProcessInstanceIncidentsQueryOptions(processInstanceId),
    enabled: !!processInstanceId,
  });
  const incidents = (incidentsResponse as any)?.data || incidentsResponse || [];
  const hasIncidents = incidents.length > 0;

  const processDisplayId = processInstance?.id || processInstanceId;
  const nup =
    processInstance?.nupExhibition || processInstance?.nup || processDisplayId;

  useEffect(() => {
    setTitlePrefix(nup || null);
    return () => setTitlePrefix(null);
  }, [nup, setTitlePrefix]);

  const processName =
    processInstance?.processDefinitionName || t("processes.process");
  const processVersion = processInstance?.processDefinitionVersion;
  const starterName =
    (starterUser as any)?.data?.name ||
    (starterUser as any)?.name ||
    processInstance?.startUserId ||
    "-";
  const startDate = formatDate(processInstance?.startTime);
  const endDate = processInstance?.endTime
    ? formatDate(processInstance?.endTime)
    : "-";
  const processVariables =
    (processVariablesResponse as any)?.data?.[processInstanceId] ||
    (processVariablesResponse as any)?.[processInstanceId] ||
    {};
  const isEmailTriggerProcess = Boolean(
    processVariables.emailProcessEventId ||
    processVariables.emailProcessDocuments ||
    processVariables.emailMessageId,
  );
  const source = isEmailTriggerProcess
    ? t("processes.emailTrigger")
    : t("processes.system");

  const availableTabs = [
    { id: "details", label: t("processes.details"), icon: <ListAlt /> },
    { id: "timeline", label: t("processes.timeline"), icon: <Timeline /> },
    { id: "documents", label: t("processes.documents"), icon: <Description /> },
    { id: "signature", label: t("processes.signature"), icon: <HistoryEdu /> },
    { id: "variables", label: "Variáveis", icon: <Settings /> },
    { id: "incidents", label: "Incidentes", icon: <Warning /> },
  ];

  const path = location.pathname;
  const currentTab = Math.max(
    0,
    availableTabs.findIndex((t) => path.includes(`/history/${t.id}`)),
  );

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      <Stack direction="row" alignItems="center" spacing={2} mb={4}>
        <IconButton onClick={handleBack} color="primary">
          <ArrowBack />
        </IconButton>
        <Typography variant="h4" fontWeight={700}>
          {t("processes.processHistory")}
        </Typography>
      </Stack>

      {isLoadingProcess ? (
        <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Typography color="error">
          {t("processes.errorLoadingProcess")}
        </Typography>
      ) : (
        <>
          {processInstance ? (
            <ProcessHeader
              nup={nup}
              processName={processName}
              processVersion={processVersion}
              starterName={starterName}
              source={source}
              startDate={startDate}
              endDate={endDate}
            />
          ) : (
            <Typography>{t("processes.processNotFound")}</Typography>
          )}

          <Box sx={{ width: "100%", mt: 3 }}>
            <Box
              sx={{
                borderBottom: 1,
                borderColor: "divider",
                mb: 2,
                position: "sticky",
                top: 0,
                bgcolor: "background.paper",
                zIndex: 10,
              }}
            >
              <Tabs
                value={currentTab}
                onChange={(_, value) => {
                  const targetTab = availableTabs[value];
                  navigate({
                    to: `/processes/$processInstanceId/history/${targetTab.id}`,
                    params: { processInstanceId },
                    search: {
                      from: search.from,
                      fromParams: search.fromParams,
                    },
                  });
                }}
                aria-label="history tabs"
                variant="scrollable"
                scrollButtons="auto"
                allowScrollButtonsMobile
              >
                {availableTabs.map((tab) => (
                  <Tab
                    key={tab.id}
                    icon={tab.icon}
                    iconPosition="start"
                    label={tab.label}
                  />
                ))}
              </Tabs>
            </Box>

            <Outlet />
          </Box>
        </>
      )}
    </Container>
  );
}
